import * as enabledApi from '@shared/api/enabledPlayApi';
import i18n from '@shared/locales/i18n';
import AssociatedProfileModel from '@shared/models/deviceProfiles/AssociatedProfileModel';
import DeviceProfileShareModel from '@shared/models/deviceProfiles/DeviceProfileShareModel';
import GenerateProfileRequest from '@shared/models/deviceProfiles/GenerateProfileRequest';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import { OSTypes } from '@shared/models/devices/OSTypes';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import dayjs, { Dayjs } from 'dayjs';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { publish, subscribe } from '../helpers/EventCallback';

interface ProfilesState {
    // State
    isLoading: boolean;
    allProfiles: UserDeviceProfileModel[];
    errors: string[];
    organizationProfiles: UserDeviceProfileModel[];
    profiles: UserDeviceProfileModel[];

    // Associated programs data (formerly module-level refs)
    associatedProgramsList: { [appName: string]: AssociatedProfileModel[] };
    appNameFuzzySearch: FuzzySearch<string> | null;
    associatedProgramsAppNames: string[];
    lastModifiedDate: Date | null;

    // Actions
    addProfile: (profile: UserDeviceProfileModel) => void;
    clearProfiles: () => void;
    createShareLink: (
        profileId: string | undefined,
        isSingleUse: boolean,
        expirationDate: Dayjs | null
    ) => Promise<IResult<DeviceProfileShareModel>>;
    deleteProfile: (profileId: string, isHidden?: boolean) => Promise<IResult<UserDeviceProfileModel>>;
    deleteShare: (shareId: string) => Promise<IResult<DeviceProfileShareModel>>;
    filterProfiles: (
        search: string,
        platform: string,
        userVoiceLocale: string,
        selectedInterest?: string
    ) => UserDeviceProfileModel[];
    fuzzySearchProfiles: (search: string) => UserDeviceProfileModel[];
    generateProfile: (request: GenerateProfileRequest) => Promise<IResult<UserDeviceProfileModel>>;
    getAssociatedAppName: (appName: string) => string | null;
    getIndicatorName: (name: string, isPublic?: boolean) => string;
    getProfiles: () => Promise<IResult<UserDeviceProfileModel[]>>;
    getShares: (profileId: string | undefined) => Promise<IResult<DeviceProfileShareModel[]>>;
    initializeFromStorage: () => void;
    isPlatformSupportedProfile: (profile: UserDeviceProfileModel, os: OSTypes) => boolean;
    removeLocalProfile: (profileId: string) => void;
    setOrganizationProfiles: (profiles: UserDeviceProfileModel[]) => void;
    updateProfile: (profile: UserDeviceProfileModel) => void;
}

const getSortedProfiles = (newProfiles: UserDeviceProfileModel[]) =>
    newProfiles.slice().sort((a, b) => a.name.localeCompare(b.name));

const getAssociationsFromProfiles = (localProfiles: UserDeviceProfileModel[]) => {
    const associations: { [appName: string]: AssociatedProfileModel[] } = {};
    const sortedProfiles = _.cloneDeep(localProfiles);
    sortedProfiles.sort((a, b) => (dayjs(a.modifiedDate) > dayjs(b.modifiedDate) ? -1 : 1));
    sortedProfiles.forEach((profile) => {
        profile.configuration.associatedPrograms?.forEach((appName) => {
            const cleansedAppName = appName.trim().toLowerCase();
            if (cleansedAppName === '') return;
            if (!(cleansedAppName in associations)) associations[cleansedAppName] = [];
            if (profile.id) {
                associations[cleansedAppName].push({
                    id: profile.id,
                    name: profile.name,
                    isPublic: profile.isPublic,
                    platform: profile.platform,
                });
            }
        });
    });
    return associations;
};

const calculateMatchScores = (searchTerm: string, names: string[]): Map<string, number> => {
    const searchParts = searchTerm.split(/[\s-]+/);
    const scoreMap = new Map<string, number>();
    for (const name of names) {
        const cleansedName = name.toLowerCase();
        const nameParts = cleansedName.split(/[\s-]+/);
        let score = 0;
        const isSequentialMatch =
            searchParts.join(' ').includes(nameParts.join(' ')) || nameParts.join(' ').includes(searchParts.join(' '));
        if (isSequentialMatch) score += 10;
        const matchCount = Math.max(
            searchParts.filter((part) => nameParts.includes(part)).length,
            nameParts.filter((part) => searchParts.includes(part)).length
        );
        score += matchCount;
        if (cleansedName.includes(searchTerm) || searchTerm.includes(cleansedName)) score += 1;
        if (score > 0) scoreMap.set(name, score);
    }
    return scoreMap;
};

const getBestMatch = (scores: Map<string, number>): string | null => {
    if (scores.size === 0) return null;
    let bestScore = 0;
    let bestMatch = null;
    for (const [name, score] of scores) {
        if (score > bestScore) {
            bestScore = score;
            bestMatch = name;
        }
    }
    return bestMatch;
};

export const useProfilesStore = create<ProfilesState>()(
    immer((set, get) => {
        const setLocalProfiles = (newProfiles: UserDeviceProfileModel[], ignoreLocalStorage: boolean = false) => {
            const sortedProfiles = getSortedProfiles(newProfiles);
            set((state) => {
                state.profiles = sortedProfiles;
            });
            if (!ignoreLocalStorage) {
                localStorage.setItem('DEVICE_PROFILES', JSON.stringify(sortedProfiles));
            }
            // Replaces the useEffect([profiles]) that combined custom + public profiles
            const { allProfiles } = get();
            const publicProfiles = allProfiles.filter((p) => p.isPublic);
            updateCombinedProfiles([...sortedProfiles, ...publicProfiles]);
        };

        const updateCombinedProfiles = (localProfiles: UserDeviceProfileModel[]) => {
            const { allProfiles } = get();
            const hasNewModifications = () => {
                if (allProfiles.length === 0) return true;
                if (allProfiles.length !== localProfiles.length) return true;
                return localProfiles.some((localProfile) => {
                    const existingProfile = allProfiles.find((p) => p.id === localProfile.id);
                    if (!existingProfile) return true;
                    return (
                        !_.isEqual(existingProfile.configuration, localProfile.configuration) ||
                        existingProfile.name !== localProfile.name ||
                        existingProfile.description !== localProfile.description
                    );
                });
            };
            if (hasNewModifications()) {
                set((state) => {
                    state.lastModifiedDate = new Date();
                    state.allProfiles = localProfiles;
                });
                const associations = getAssociationsFromProfiles(localProfiles);
                const appNames = Object.keys(associations);
                set((state) => {
                    state.associatedProgramsList = associations;
                    state.associatedProgramsAppNames = appNames;
                    state.appNameFuzzySearch = new FuzzySearch(appNames, [], { sort: true, caseSensitive: false });
                });
            }
        };

        const isPlatformCompatible = (
            devicePlatform: string | undefined,
            profilePlatform: string = 'generic'
        ): boolean => {
            const normalizedDevicePlatform = devicePlatform?.toLowerCase();
            if (!devicePlatform || normalizedDevicePlatform === 'generic') return true;
            const normalizedProfilePlatform = profilePlatform.toLowerCase();
            return normalizedDevicePlatform === normalizedProfilePlatform || normalizedProfilePlatform === 'generic';
        };

        const getIndicatorName = (name: string, isPublic?: boolean) =>
            !isPublic ? i18n.t('common:profile.custom', { name }) : name;

        const setOrganizationProfiles = (profiles: UserDeviceProfileModel[]) =>
            set((state) => {
                state.organizationProfiles = profiles;
            });

        const initializeFromStorage = () => {
            try {
                const localProfiles = localStorage.getItem('DEVICE_PROFILES');
                if (localProfiles) {
                    setLocalProfiles(JSON.parse(localProfiles), true);
                }
            } catch (e) {
                console.log(e);
            }
        };

        const getProfiles = async () => {
            try {
                set((state) => {
                    state.isLoading = true;
                });
                const result = await enabledApi.getDeviceProfiles();
                set((state) => {
                    state.isLoading = false;
                });
                if (result?.resultType === 'Ok' && result.data) {
                    setLocalProfiles(result.data);
                }
                return result;
            } catch (e) {
                set((state) => {
                    state.isLoading = false;
                    state.errors = ['Error getting profiles'];
                });
                console.log(e);
                return new UnexpectedResult<UserDeviceProfileModel[]>();
            }
        };

        const deleteProfile = async (profileId: string, isHidden?: boolean) => {
            try {
                const result = await enabledApi.deleteProfile(profileId);
                if (result?.resultType === 'Ok') {
                    const currentProfiles = get().profiles;
                    const newProfiles = currentProfiles.filter((x) => x.id !== profileId);
                    const profile = currentProfiles.find((x) => x.id === profileId);
                    publish('profileDeleted', { profile });
                    setLocalProfiles(newProfiles);
                    if (!isHidden) {
                        publish('alertSuccess', {
                            message: i18n.t('common:profile.success.delete'),
                            timeout: 5000,
                        });
                    }
                }
                return result;
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error deleting profile'];
                });
                return new UnexpectedResult<UserDeviceProfileModel>();
            }
        };

        const addProfile = (profile: UserDeviceProfileModel) => {
            const currentProfiles = get().profiles;
            if (currentProfiles.find((x) => x.id === profile.id)) {
                get().updateProfile(profile);
            } else {
                setLocalProfiles([...currentProfiles, profile]);
            }
        };

        const updateProfile = (profile: UserDeviceProfileModel) => {
            const currentProfiles = get().profiles;
            const existingProfile = currentProfiles.find((x) => x.id === profile.id);
            if (existingProfile && _.isEqual(existingProfile, profile)) return;
            const newProfiles = currentProfiles.map((x) => (x.id === profile.id ? profile : x));
            setLocalProfiles(newProfiles);
        };

        const removeLocalProfile = (profileId: string) => {
            const newProfiles = get().profiles.filter((x) => x.id !== profileId);
            setLocalProfiles(newProfiles);
        };

        const createShareLink = async (
            profileId: string | undefined,
            isSingleUse: boolean,
            expirationDate: Dayjs | null
        ) => {
            if (!profileId) return new UnexpectedResult<DeviceProfileShareModel>();
            try {
                const result = await enabledApi.shareProfile(profileId, {
                    expirationDate: expirationDate?.toISOString(),
                    isSingleUse,
                    isPublic: true,
                });
                if (result?.resultType === 'Ok') {
                    publish('alertSuccess', { message: i18n.t('common:profile.success.share'), timeout: 5000 });
                }
                return result;
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error creating share link'];
                });
                return new UnexpectedResult<DeviceProfileShareModel>();
            }
        };

        const getShares = async (profileId: string | undefined) => {
            if (!profileId) return new UnexpectedResult<DeviceProfileShareModel[]>();
            try {
                return await enabledApi.getProfileShares(profileId);
            } catch (e) {
                set((state) => {
                    state.errors = ['Error getting shares'];
                });
                return new UnexpectedResult<DeviceProfileShareModel[]>();
            }
        };

        const deleteShare = async (shareId: string) => {
            try {
                const result = await enabledApi.disableProfileShare(shareId);
                if (result?.resultType === 'Ok') {
                    publish('alertSuccess', {
                        message: i18n.t('common:profile.success.shareDelete'),
                        timeout: 5000,
                    });
                }
                return result;
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error deleting share link'];
                });
                return new UnexpectedResult<DeviceProfileShareModel>();
            }
        };

        const generateProfile = async (request: GenerateProfileRequest) => {
            try {
                const result = await enabledApi.generateUserDeviceProfile(request);
                if (result?.resultType === 'Ok') {
                    publish('alertSuccess', { message: i18n.t('common:profile.success.generate'), timeout: 5000 });
                }
                return result;
            } catch (e) {
                console.log(e);
                set((state) => {
                    state.errors = ['Error generating profile'];
                });
                return new UnexpectedResult<UserDeviceProfileModel>();
            }
        };

        const clearProfiles = () => {
            set((state) => {
                state.profiles = [];
                state.allProfiles = [];
            });
            localStorage.removeItem('DEVICE_PROFILES');
        };

        const fuzzySearchProfiles = (search: string) => {
            const searcher = new FuzzySearch(
                get().allProfiles,
                ['name', 'description', 'configuration.associatedPrograms'],
                { sort: true, caseSensitive: false }
            );
            return searcher.search(search);
        };

        const isPlatformSupportedProfile = (profile: UserDeviceProfileModel, os: OSTypes) => {
            if (!profile) return false;
            return !profile.platform || profile.platform === os || profile.platform === 'generic';
        };

        const getAssociatedAppName = (appName: string) => {
            const cleansedAppName = appName.trim().toLowerCase();
            if (!cleansedAppName) return null;
            const { appNameFuzzySearch, associatedProgramsAppNames } = get();
            const fuzzyResults = appNameFuzzySearch?.search(cleansedAppName);
            if (fuzzyResults?.length) return fuzzyResults[0];
            const exactMatch = associatedProgramsAppNames.find((name) => name.toLowerCase() === cleansedAppName);
            if (exactMatch) return exactMatch;
            const scores = calculateMatchScores(cleansedAppName, associatedProgramsAppNames);
            return getBestMatch(scores);
        };

        const filterProfiles = (
            search: string,
            platform: string,
            userVoiceLocale: string,
            selectedInterest?: string
        ) => {
            const normalizedFilter = search.toLowerCase().trim();
            const normalizedInterest = selectedInterest?.toLowerCase().trim();
            const { allProfiles } = get();
            return allProfiles.filter((profile) => {
                if (!profile || !isPlatformCompatible(platform, profile.platform)) return false;
                if (
                    profile.deviceProfileGroupId &&
                    profile.defaultLocale !== userVoiceLocale &&
                    !profile.isDefaultInGroup
                )
                    return false;
                if (
                    profile.deviceProfileGroupId &&
                    profile.isDefaultInGroup &&
                    profile.defaultLocale !== userVoiceLocale
                ) {
                    const hasCurrentLocale = profile.deviceProfileGroup?.some((m) => m.locale === userVoiceLocale);
                    if (hasCurrentLocale) return false;
                }
                const fields = [
                    getIndicatorName(profile.name, profile.isPublic).toLowerCase(),
                    profile.description?.toLowerCase() || '',
                    ...(profile.configuration.associatedPrograms?.map((p) => p.toLowerCase()) || []),
                ];
                const originated = allProfiles.find((p) => p.id === profile.originatedByProfileId);
                if (originated) {
                    fields.push(
                        getIndicatorName(originated.name, originated.isPublic).toLowerCase(),
                        originated.description?.toLowerCase() || '',
                        ...(originated.configuration.associatedPrograms?.map((p) => p.toLowerCase()) || [])
                    );
                }
                const matchesFilter = fields.some((field) => field.includes(normalizedFilter));
                const matchesInterest =
                    !normalizedInterest || profile.interests.map((i) => i.toLowerCase()).includes(normalizedInterest);
                return matchesFilter && matchesInterest;
            });
        };

        return {
            // State
            profiles: [],
            allProfiles: [],
            organizationProfiles: [],
            isLoading: false,
            errors: [],

            // Associated programs data
            associatedProgramsList: {},
            appNameFuzzySearch: null,
            associatedProgramsAppNames: [],
            lastModifiedDate: null,

            // Actions
            addProfile,
            clearProfiles,
            createShareLink,
            deleteProfile,
            deleteShare,
            filterProfiles,
            fuzzySearchProfiles,
            generateProfile,
            getAssociatedAppName,
            getIndicatorName,
            getProfiles,
            getShares,
            initializeFromStorage,
            isPlatformSupportedProfile,
            removeLocalProfile,
            setOrganizationProfiles,
            updateProfile,
        };
    })
);

// Module-level event subscriptions (replace useEffect([], []))
subscribe('default-profiles-updated', (data: UserDeviceProfileModel[]) => {
    const store = useProfilesStore.getState();
    const combined = [...store.profiles, ...data];
    const associations = getAssociationsFromProfiles(combined);
    const appNames = Object.keys(associations);
    useProfilesStore.setState((state) => {
        state.allProfiles = combined;
        state.associatedProgramsList = associations;
        state.associatedProgramsAppNames = appNames;
        state.appNameFuzzySearch = new FuzzySearch(appNames, [], { sort: true, caseSensitive: false });
    });
});

subscribe('userProfileAdded', (data: { profile: UserDeviceProfileModel }) => {
    useProfilesStore.getState().addProfile(data.profile);
});

subscribe('userProfileUpdated', (data: { profile: UserDeviceProfileModel }) => {
    useProfilesStore.getState().updateProfile(data.profile);
});

subscribe('userProfileDeleted', (data: { profileId: string }) => {
    useProfilesStore.getState().deleteProfile(data.profileId, true);
});
